import {
  FlexBox,
  FluidImage,
  Li,
  Link,
  ModalDialog,
  ModalDialogBody,
  ModalDialogCloseButton,
  ModalDialogContent,
  ModalDialogHeader,
  ModalDialogNav,
  Typography,
  Ul,
} from '@vp/swan'
import type { AbsoluteGuaranteeModal as AbsoluteGuaranteeModalType } from '../../../types/site-footer'

interface AbsoluteGuaranteeModalProps {
  modal: AbsoluteGuaranteeModalType
  isOpen: boolean
  onDismiss: () => void
}

type MarkdownBlock =
  { type: 'heading'; text: string } | { type: 'list'; items: string[] } | { type: 'paragraph'; text: string; link?: { href: string; text: string } }

/**
 * Parses a limited subset of markdown used in CMS-controlled modal body content.
 *
 * Supported syntax:
 * - `## heading` → rendered as title-section Typography
 * - `- item` or `* item` → rendered as SWAN Ul/Li list
 * - `[text](url)` (standalone) → rendered as SWAN Link
 * - `---` → skipped (horizontal rule separator)
 * - `[text](url)` (inline within paragraph) → stripped to plain text
 * - All other text → rendered as body-standard Typography paragraph
 */
function parseMarkdown(md: string): MarkdownBlock[] {
  return md
    .split(/\n\n+/)
    .map((block): MarkdownBlock | null => {
      const trimmed = block.trim()
      if (!trimmed) return null
      if (trimmed === '---') return null
      if (trimmed.startsWith('## ')) {
        return { type: 'heading', text: trimmed.slice(3) }
      }
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        const items = trimmed.split(/\n/).map((line) => line.replace(/^[-*]\s+/, '').trim())
        return { type: 'list', items }
      }
      // Check for link
      const linkMatch = trimmed.match(/\[([^\]]+)\]\(([^)]+)\)/)
      if (linkMatch && trimmed.replace(/\[([^\]]+)\]\(([^)]+)\)/, '').trim() === '') {
        return { type: 'paragraph', text: linkMatch[1]!, link: { href: linkMatch[2]!, text: linkMatch[1]! } }
      }
      // Plain paragraph
      const plainText = trimmed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
      return { type: 'paragraph', text: plainText }
    })
    .filter((b): b is MarkdownBlock => b !== null)
}

function MarkdownBody({ body }: { body: string }) {
  const blocks = parseMarkdown(body)

  return (
    <>
      {blocks.map((block, i) => {
        switch (block.type) {
          case 'heading':
            return (
              <Typography key={i} fontSkin="title-section" mt={i > 0 ? '7' : undefined} mb="4" style={{ textTransform: 'none' }}>
                {block.text}
              </Typography>
            )
          case 'list':
            return (
              <Ul key={i}>
                {block.items.map((item, j) => (
                  <Li key={j}>{item}</Li>
                ))}
              </Ul>
            )
          case 'paragraph':
            if (block.link) {
              return (
                <Link key={i} href={block.link.href} mt="4">
                  {block.link.text}
                </Link>
              )
            }
            return (
              <Typography key={i} fontSkin="body-standard" mt="4">
                {block.text}
              </Typography>
            )
        }
      })}
    </>
  )
}

export const AbsoluteGuaranteeModal: React.FC<AbsoluteGuaranteeModalProps> = ({ modal, isOpen, onDismiss }) => {
  return (
    <ModalDialog isOpen={isOpen} onRequestDismiss={onDismiss}>
      <ModalDialogContent aria-label={modal.title} standardMode>
        <ModalDialogNav>
          <ModalDialogCloseButton />
        </ModalDialogNav>
        <ModalDialogHeader style={{ maxWidth: '100%' }}>
          <FlexBox alignItems="center" gap="3" style={{ width: '100%' }}>
            {modal.imageUrl && (
              <FluidImage
                src={modal.imageUrl}
                alt={modal.imageAltText ?? ''}
                style={{ maxWidth: '69.3px', maxHeight: '63px', flexShrink: 0, alignSelf: 'flex-start' }}
              />
            )}
            <Typography fontSkin="title-headline" style={{ textTransform: 'uppercase', margin: 0 }}>
              {modal.title}
            </Typography>
          </FlexBox>
        </ModalDialogHeader>
        <ModalDialogBody>
          <MarkdownBody body={modal.body} />
        </ModalDialogBody>
      </ModalDialogContent>
    </ModalDialog>
  )
}
